import isNil from 'lodash/isNil'
import type {
  AdjustmentLineDto,
  FundingInvoiceDetailDto,
  FundingInvoiceYtdLineType,
  HealthPlanLineDto,
} from '../../../api/dto/generated-invoice.dto'

export interface InvoiceLineSplit {
  /** Health Plans tab: the funded month's premium lines. */
  premiums: HealthPlanLineDto[]
  /** Adjustments tab: credits, other-month lines and funded-month corrections. */
  adjustments: AdjustmentLineDto[]
  /** Plan Costs on the Invoice Summary, in dollars. */
  planCosts: number
  /** Net of the Adjustments tab, in dollars; negative when credits dominate. */
  adjustmentsTotal: number
  /** True when the response predates line categories and the split was derived here. */
  isDerived: boolean
}

const sumInvoiceAmounts = (
  rows: ReadonlyArray<{ planCost: { invoice: number } }>
): number => rows.reduce((total, row) => total + row.planCost.invoice, 0)

// Credits are stored negative (ADR-0002), which is the only type signal an
// older Health Plans row carries.
const toLineType = (row: HealthPlanLineDto): FundingInvoiceYtdLineType =>
  row.planCost.invoice < 0 ? 'CREDIT' : 'CHARGE'

/**
 * Splits an invoice's health-plan lines into the Health Plans and Adjustments
 * tabs. The API decides the split (`detail.adjustments`, stamped per line at
 * generation time) and reports the two subtotals on the summary. Until every
 * environment serves that shape, an older response is split here with the same
 * rule the backend applies to uncategorised lines: a charge for the funded
 * month is a premium, everything else is an adjustment.
 */
export const splitInvoiceLines = (
  detail: FundingInvoiceDetailDto,
  fundedMonth: string | null
): InvoiceLineSplit => {
  const healthPlans = detail.healthPlans ?? []
  if (!isNil(detail.adjustments)) {
    const { adjustments } = detail
    return {
      premiums: healthPlans,
      adjustments,
      planCosts: detail.summary.planCosts ?? sumInvoiceAmounts(healthPlans),
      adjustmentsTotal:
        detail.summary.adjustments ?? sumInvoiceAmounts(adjustments),
      isDerived: false,
    }
  }
  const isFundedMonthCharge = (row: HealthPlanLineDto): boolean =>
    (isNil(fundedMonth) || row.billingMonth === fundedMonth) &&
    row.planCost.invoice >= 0
  const premiums = healthPlans.filter(isFundedMonthCharge)
  const adjustments = healthPlans
    .filter((row) => !isFundedMonthCharge(row))
    .map((row) => ({ ...row, type: toLineType(row) }))
  return {
    premiums,
    adjustments,
    planCosts: sumInvoiceAmounts(premiums),
    adjustmentsTotal: sumInvoiceAmounts(adjustments),
    isDerived: true,
  }
}
